import { ProfileSectionTabs } from './components/profile-section-tabs';
import { ProfileSectionContent } from './components/profile-section-content';
import { ProfileScreenSheets } from './components/profile-screen-sheets';
import { usePlaceProfileState, useProfileActions } from './components/use-profile-screen-state';
import type { ProfileTab } from './components/profile-section-types';
import { ProfileTrustMembership } from './components/profile-trust';
import { isLoggedInAtom, userInfoAtom } from 'app/atoms';
import UnavailabilityBanner from 'app/components/unavailability-banner';
import { Column } from 'app/design/primitives/box';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useIsCurrentlyUnavailable } from 'app/hooks/use-is-currently-unavailable';
import { useIsExpired } from 'app/hooks/use-is-expired';
import { useCanUseServices } from 'app/network/category';
import { useProfile } from 'app/network/profile';
import { useStatistics } from 'app/network/statistics';
import type { NavigationScreen } from 'app/types/navigation';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { useUISize } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useKeyboard } from '@react-native-community/hooks';
import {
  ACTIVITY_SECTOR_FEATURE,
  getProfileAccessory,
  getProfileNavigationOptions,
  getProfileStats,
  isViewedAmbassador,
  isViewingOwnProfile,
  openPlaceItinerary,
  shareNeighbourProfile,
  sharePlaceProfile,
  shouldShowSuperSharer,
  theme,
  useProfileListingsData,
  useProfilePageTitle,
} from './components/shared';
import { ProfileBadge } from './components/profile-badge';
import { ProfileStatsAndReport } from './components/profile-stats-and-report';
import { ProfileRatingsSection } from './components/profile-ratings-section';
import { PlaceDiscountBanner } from './components/place-discount-banner';
import { PlaceCover } from './components/place-cover';
import { ProfileHero } from './components/profile-hero';
import { ProfileBio } from './components/profile-bio';
import { ProfileActivitySectors } from './components/profile-activity-sectors';
import { PlaceInfo } from './components/place-info';

function useProfileMetadata(
  profileQuery: ReturnType<typeof useProfile>,
  statisticsQuery: ReturnType<typeof useStatistics>,
) {
  useRegisterLoading(profileQuery.isPending, 'neighbour-profile-profile');
  useRegisterLoading(statisticsQuery.isPending, 'neighbour-profile-statistics');

  const ratings = profileQuery.data?.ratings ?? [];
  return {
    ratedSubjectName: [profileQuery.data?.firstName, profileQuery.data?.lastName].filter(Boolean).join(' ').trim(),
    ratings,
  };
}

export const MemberProfileScreen: NavigationScreen<'neighbour-profile'> = ({ navigation, route }) => {
  const { t } = useTranslation();
  const userInfo = useAtomValue(userInfoAtom);
  const { isEnabled: ambassadorEnabled } = useAmbassador();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isMyself = isViewingOwnProfile(userInfo?.id, route.params.userId);
  const ns = isMyself ? 'my-profile' : 'neighbour-profile';
  const statisticsQuery = useStatistics(route.params.userId);
  const canUseServices = useCanUseServices();
  const canUseDonations = Boolean(userInfo?.canPublishDonations);
  const canUseSales = isFeatureEnabled(FeatureFlag.showSalesOnPublicProfile, userInfo?.featureFlags);
  const { isSmallUI, isLargeUI } = useUISize();
  const canPublishListings = statisticsQuery.data?.canPublish;
  const sharingPlacesEnabled = isFeatureEnabled(FeatureFlag.sharingPlaces, userInfo?.featureFlags);
  const profileQuery = useProfile(route.params.userId, {
    throwOnError: true,
  });
  const insets = useSafeAreaInsets();
  const horizontalPadding = insets.left || theme.spacing['spacing.general.x4'];
  const keyboard = useKeyboard();
  const place = usePlaceProfileState({
    isLoggedIn,
    isMyself,
    profile: profileQuery.data,
    t,
    userId: route.params.userId,
  });
  const {
    hasWebsite,
    isFrequenting,
    isJoining,
    isLeaving,
    isPlace,
    nextOpenDay,
    openingStatus,
    placeDetails,
    placeMembers,
    setShowDiscount,
    setShowJoinConfirm,
    setShowLeaveConfirm,
    setShowOpeningHours,
    setShowPlaceHelp,
    setShowPlaceMembers,
    websiteUrl,
  } = place;
  const [showProfileActions, setShowProfileActions] = useState(false);
  const [profileTab, setProfileTab] = useState<ProfileTab>('overview');
  const listings = useProfileListingsData({
    profileTab,
    isLoggedIn,
    isMyself,
    isPlace,
    isSmallUI,
    userId: route.params.userId,
  });
  useNavigationOptions(
    navigation,
    getProfileNavigationOptions({
      isLargeUI,
      isPlace,
      isRoot: navigation.getState().index === 0,
      ns,
      onMore: () => setShowProfileActions(true),
      onHelp: () => setShowPlaceHelp(true),
      t,
    }),
  );

  const openItinerary = () => openPlaceItinerary(placeDetails);
  const sharePlace = () => sharePlaceProfile(websiteUrl, profileQuery.data);
  const shareProfile = () => shareNeighbourProfile(profileQuery.data, t);
  const isUnavailable = useIsCurrentlyUnavailable(profileQuery.data?.unavailability);
  useProfilePageTitle({ isPlace, ns, profile: profileQuery.data, t });

  const authorize = useIsExpired(true);
  const profileActions = useProfileActions({ authorize, navigation, profile: profileQuery.data, route });
  const {
    channelMutationError,
    contactButtonHandler,
    isContactPending,
    isImageViewerModalOpen,
    setIsAmbassadorSheetOpen,
    setIsImageViewerModalOpen,
  } = profileActions;
  const showSuperPartageur = shouldShowSuperSharer(profileQuery.data);
  const viewedProfileIsAmbassador = isViewedAmbassador(ambassadorEnabled, profileQuery.data?.ambassadorStatus);
  const badge = (
    <ProfileBadge
      userId={route.params.userId}
      avatarSize={profileQuery.data?.placeDetails ? 88 : 72}
      {...{ showSuperPartageur, viewedProfileIsAmbassador }}
    />
  );

  const canRateAfterLoans = isFeatureEnabled(FeatureFlag.rateAfterLoans, userInfo?.featureFlags);

  const emojiStatsElements = getProfileStats({
    canPublishListings,
    canRateAfterLoans,
    canUseDonations,
    canUseSales,
    canUseServices,
    statistics: statisticsQuery.data,
    t,
  });

  const { ratedSubjectName, ratings } = useProfileMetadata(profileQuery, statisticsQuery);

  const defaultPaddingStyle = usePagePadding();

  const paddingHorizontal = defaultPaddingStyle.paddingLeft;

  const ratingsSection = (
    <ProfileRatingsSection
      currentUserId={userInfo?.id}
      isMyself={isMyself}
      navigation={navigation}
      ratedUserId={route.params.userId}
      ratings={ratings}
      subjectName={ratedSubjectName}
    />
  );

  return (
    <>
      <Page
        showsVerticalScrollIndicator={false}
        onScroll={listings.pagination.handlePageScroll}
        onScrollBeginDrag={listings.pagination.handlePageScrollBeginDrag}
        scrollEventThrottle={16}
        accessory={getProfileAccessory({
          canReceiveInAppContact: placeDetails?.allowInAppContact !== false,
          channelMutationError,
          contactButtonHandler,
          isContactPending,
          isMyself,
          isPlace,
          keyboardShown: keyboard.keyboardShown,
          ns,
          onShareProfile: () => void shareProfile(),
          paddingHorizontal,
          t,
        })}

        paddingStyle={{
          ...defaultPaddingStyle,
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: theme.spacing['spacing.general.x12'],
        }}
      >
        {isUnavailable ? <UnavailabilityBanner /> : null}
        <Column>
          <PlaceDiscountBanner onOpen={() => setShowDiscount(true)} placeDetails={placeDetails} />
          <PlaceCover placeDetails={placeDetails} />

          <Column
            style={{
              width: '100%',
              paddingHorizontal: theme.spacing[isPlace ? 'spacing.general.x5' : 'spacing.general.x4'],
              paddingTop: theme.spacing['spacing.general.x2'],
              paddingBottom: theme.spacing['spacing.general.x2'],
              gap: theme.spacing[isPlace ? 'spacing.general.x2' : 'spacing.general.x4'],
              borderRadius: theme.borderRadii['radius.lg'],
              borderColor: theme.colors['surface.disabled'],
              backgroundColor: theme.colors['surface.default'],
            }}
          >
            <ProfileHero
              badge={badge}
              isImageViewerModalOpen={isImageViewerModalOpen}
              isUnavailable={isUnavailable}
              onCloseImage={() => setIsImageViewerModalOpen(false)}
              onOpenAmbassador={() => setIsAmbassadorSheetOpen(true)}
              onOpenImage={() => setIsImageViewerModalOpen(true)}
              profile={profileQuery.data}
              showSuperPartageur={showSuperPartageur}
              trustPoints={statisticsQuery.data?.loans.trustPoints}
              subscribed={statisticsQuery.data?.subscribed}
              viewedProfileIsAmbassador={viewedProfileIsAmbassador}
            />
            <ProfileBio profile={profileQuery.data} />
            <PlaceInfo
              horizontalPadding={horizontalPadding}
              members={placeMembers}
              nextOpenDay={nextOpenDay}
              onFrequentingPress={() => (isFrequenting ? setShowLeaveConfirm(true) : setShowJoinConfirm(true))}
              onHelp={() => setShowPlaceHelp(true)}
              onItinerary={openItinerary}
              onOpenMembers={() => setShowPlaceMembers(true)}
              onOpenOpeningHours={() => setShowOpeningHours(true)}
              onShare={() => void sharePlace()}
              openingStatus={openingStatus}
              placeDetails={isPlace ? placeDetails : null}
              variant={{
                membership: isFrequenting ? 'frequenting' : 'not-frequenting',
                membershipAction: isJoining || isLeaving ? 'pending' : 'idle',
                viewer: isMyself ? 'self' : 'visitor',
                website: hasWebsite ? 'available' : 'unavailable',
              }}
              websiteUrl={websiteUrl}
            />
            {!isPlace ? (
              <ProfileTrustMembership
                profileQuery={profileQuery}
                navigation={navigation}
                userId={route.params.userId}
                isMyself={isMyself}
              />
            ) : null}

            <ProfileActivitySectors
              enabled={isFeatureEnabled(ACTIVITY_SECTOR_FEATURE, userInfo?.featureFlags)}
              sectors={profileQuery.data?.activitySectors}
            />
            <ProfileStatsAndReport emojiStatsElements={isPlace ? emojiStatsElements : []} />
          </Column>

          <ProfileSectionTabs
            ratingsCount={ratings.length}
            {...{ isPlace, profileTab, listings, canRateAfterLoans, sharingPlacesEnabled, ns, setProfileTab }}
          />
          <ProfileSectionContent
            features={{ ratings: canRateAfterLoans, places: sharingPlacesEnabled }}
            {...{
              isPlace,
              profileTab,
              profileQuery,
              statisticsQuery,
              navigation,
              isMyself,
              ratingsSection,
              listings,
              isLargeUI,
            }}
            userId={route.params.userId}
            onReviews={() => setProfileTab('reviews')}
          />
        </Column>
      </Page>

      <ProfileScreenSheets
        {...{
          profileActions,
          place,
          isMyself,
          isPlace,
          navigation,
          showProfileActions,
          setShowProfileActions,
          shareProfile,
        }}
        userId={route.params.userId}
      />
    </>
  );
};
